import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { shouldCaptureApiFailure } from '@/platform/auth/authFailureCircuit';
import { captureError, captureSignal } from './error-telemetry';

type TimedRequestConfig = InternalAxiosRequestConfig & {
  _telemetryStartedAt?: number;
};

const SLOW_REQUEST_THRESHOLD_MS = 500;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function duration(config?: InternalAxiosRequestConfig): number | undefined {
  const startedAt = (config as TimedRequestConfig | undefined)?._telemetryStartedAt;
  if (startedAt === undefined) return undefined;
  return Math.max(0, Math.round(now() - startedAt));
}

function captureDuration(
  config: InternalAxiosRequestConfig | undefined,
  status: number | string,
  client: string
): void {
  const durationMs = duration(config);
  const url = config?.url ?? 'unknown';
  if (
    durationMs === undefined ||
    durationMs < SLOW_REQUEST_THRESHOLD_MS ||
    url.includes('/telemetry/')
  ) {
    return;
  }

  captureSignal('api.request.slow', {
    client,
    duration_ms: durationMs,
    method: config?.method,
    status,
    url: url.split('?', 1)[0],
    threshold_ms: SLOW_REQUEST_THRESHOLD_MS,
  });
}

/** Attach a monotonic start time to any Axios client request. */
export function markApiRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  (config as TimedRequestConfig)._telemetryStartedAt = now();
  return config;
}

/** Report actionable latency without turning every healthy request into telemetry noise. */
export function captureApiResponse(response: AxiosResponse, client: string): void {
  try {
    captureDuration(response.config, response.status, client);
  } catch {
    // Observability must never break the response chain.
  }
}

/** Canonical Axios failure boundary shared by the product and auth clients. */
export function captureApiFailure(error: AxiosError, client: string): void {
  try {
    const url = error.config?.url ?? '';
    const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
    if (
      shouldCaptureApiFailure({
        url,
        status: error.response?.status,
        networkError: isNetworkError,
      })
    ) {
      captureError(error, {
        client,
        status: error.response?.status,
        url: url.split('?', 1)[0],
        method: error.config?.method,
        code: error.code,
      });
    }
    captureDuration(error.config, error.response?.status ?? error.code ?? 'NETWORK', client);
  } catch {
    // Observability must never break the rejection chain.
  }
}
